/**
 * `/newsletter` — send an article to members on WhatsApp
 * (flow-loops-whatsapp, 2026-10-01).
 *
 * Every member it reaches gets an approved MARKETING template: their first
 * name, the line staff write about the article, a "Read the article" button
 * that opens it, and a "Book a session" button. What it says
 * (whatsapp-newsletter-audiences, 2026-10-29): "Article" is
 * `member_article_and_booking_v2`, which closes with a nudge to book;
 * "Article + offer" is `member_article_offer_v2`, which closes with "This
 * week only: <offer>." The offer is picked from the studio's discount codes
 * that anyone may spend now (`list_stripe_coupons`), which writes an offer
 * line staff can edit; what is sent is the line as it stands.
 *
 * The offer on its own (whatsapp-newsletter-offer-only, 2026-10-29): the
 * message choice is "Article" or "Offer", and "Offer" carries an "Include the
 * article" box, ticked by default. Unticked, the message is `offer` —
 * `member_offer_v2`, "… This week only: <offer>. Tap Tell me more for the
 * details." with a "Tell me more" button — the article fields go away, and
 * the discount code must be picked: nobody already sent that code is sent it
 * again, and a tap on the button starts a conversation about it with the chat
 * agent.
 *
 * The v2 templates (newsletter-v2-not-interested, 2026-10-31), after Meta
 * flagged the studio's account for "Sending spam": every message opens "Hi
 * {first name}, it's <studio's name>, your EMS studio." — the name from
 * settings, which the preview shows from whoami — and carries a "Not
 * interested" button under the footer "Not interested? Tap below and we'll
 * stop these messages". A tap opts that member out of all marketing and is
 * answered in one line. The preview shows the footer and every button.
 *
 * A studio's own words (newsletter-studio-words, 2026-11-01): a message may
 * be sent with more than one template — the shared one above, an EMS
 * studio's to its members, or a studio's own, like Clavis's
 * `clavis_update_v1` to studio owners, whose link opens any useclavis.ai page.
 * The studio's catalog lists one, and `get_whatsapp_newsletter_setup` says
 * which, so the preview shows the studio's template's words and buttons, and
 * the hints name the address its link opens. A message the studio cannot
 * send yet says why instead of showing someone else's words.
 *
 * A WhatsApp message cannot be recalled, so the page is built around one
 * rule: NOTHING SENDS UNTIL STAFF HAVE SEEN AND TYPED THE NUMBER IT WILL
 * REACH. "Check" runs `preview_whatsapp_newsletter` — the audience counted
 * from live data, why the rest are left out, and the exact link members will
 * open with what it answers. Send stays locked until the count is typed back,
 * and `send_whatsapp_newsletter` refuses if the audience has moved since.
 * Editing the audience, the article or the line throws the check away.
 * While the studio's WhatsApp marketing is halted (the /config switch,
 * wa-marketing-halt, 2026-11-03), Check says until when and Send stays away.
 *
 * Who (newsletter-tag-audience, 2026-10-02; several tags and newsletters,
 * whatsapp-newsletter-audiences, 2026-10-29): everyone, customers, leads, the
 * members carrying ANY of the tags picked, or everyone ANY of the email
 * newsletters picked went to, less anyone who unsubscribed from one. Each
 * list is chips with a searchable dropdown to add one (`<SearchableSelect>`
 * over `GET /api/lookups/tags` — every tag A–Z with how many active members
 * carry it — and over `list_newsletter_editions` with status `sent`). Someone
 * in several tags or newsletters is one member, sent once. A pasted article
 * LINK that is not the page members would open — a press article on another
 * site, say — is named after Check and holds Send back: the template's button
 * can only open the address Meta approved for it, with the slug on the end.
 *
 * Check also shows Meta's side (newsletter-meta-limit-guard, 2026-10-02):
 * Meta lets the studio message only so many different people a day, and once
 * that is used it refuses the cart reminders and staff alerts too. The line
 * shows the limit, who was already sent a template in the last 24 hours, the
 * tenth kept back and the room left; Send stays locked when the audience is
 * bigger than the room, when Meta's limit cannot be read, when Meta blocks
 * the number or rates it red, or while another newsletter is still sending —
 * the same verdict the send enforces.
 *
 * NO BESPOKE ENDPOINT: all six actions go through `dispatchAction` →
 * `POST /api/dispatch/:action` as `human:staff` (`api: []` in the page
 * registry, the same posture as `/errors` and `/reports`); the tag list is
 * the dashboard's ungated picker feed, `/api/lookups/tags`. The send itself
 * runs in the background (`send_whatsapp_newsletter` saga), so the history
 * below is what shows its progress: it re-reads `list_whatsapp_newsletter_sends`
 * every few seconds while a send is running.
 *
 * "Sent" is what WhatsApp accepted and has not refused since. Delivered and
 * Read come from WhatsApp's own reports, which keep arriving after the send is
 * done — and WhatsApp can refuse a marketing message after accepting it. The
 * history then counts it as Failed, not Sent (newsletter-late-refusals,
 * 2026-11-01), so Sent + Skipped + Failed (+ "to go" while sending) is
 * everyone the send was for. Failed's hover splits what WhatsApp refused when
 * sent from what it refused after accepting, with WhatsApp's reason.
 *
 * Under "Who", the history keeps the rest of Check's line
 * (newsletter-left-out-counts, 2026-10-30): who the audience left out at send
 * time, by reason, as the send counted them — "Left out 15: 3 opted out of
 * marketing · 12 no WhatsApp number". A send from before migration 268 kept no
 * such counts and shows none.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  WHATSAPP_NEWSLETTER_AUDIENCE_NEWSLETTERS_MAX,
  WHATSAPP_NEWSLETTER_AUDIENCE_TAGS_MAX,
  WHATSAPP_NEWSLETTER_AUDIENCES,
  WHATSAPP_NEWSLETTER_BLURB_MAX_CHARS,
  WHATSAPP_NEWSLETTER_OFFER_LINE_MAX_CHARS,
  WHATSAPP_NEWSLETTER_SKIP_REASONS,
  WHATSAPP_NEWSLETTER_SLUG_MAX_CHARS,
  WHATSAPP_NEWSLETTER_SLUG_PATTERN,
  WHATSAPP_NEWSLETTER_TEMPLATE_COPY,
  normalizeWhatsAppNewsletterBlurb,
  whatsAppNewsletterCopyBodyParts,
  whatsAppNewsletterMessageHasArticle,
  whatsAppNewsletterMessageHasOffer,
  whatsAppNewsletterPastedLinkMismatch,
  whatsAppNewsletterSlugFromInput,
  type WhatsAppMetaCanSendMessage,
  type WhatsAppMetaQualityRating,
  type WhatsAppNewsletterMetaVerdict,
  type WhatsAppNewsletterMetaWarning,
  type WhatsAppNewsletterAudience,
  type WhatsAppNewsletterMessage,
  type WhatsAppNewsletterMessageCopy,
  type WhatsAppNewsletterSkipReason,
} from "@layer/canonical-data/whatsapp-newsletter";
import { AudienceListPicker } from "../components/AudienceListPicker";
import { useWhoami } from "../components/DashboardLayout";
import { SearchableSelect, type SearchableSelectOption } from "../components/SearchableSelect";
import { api } from "../lib/api";
import { dispatchAction, generateRequestIdV7 } from "../lib/dispatch";
import type { NewsletterEditionsList } from "../lib/newsletter-editions";
import { useCachedResource } from "../lib/resource-cache";
import { studioTimeZone } from "../lib/studio-tz";
import { useSagaRun } from "../lib/use-saga-run";
import {
  offerLineFromCoupon,
  offerOptionMeta,
  offerableCoupons,
  type OfferCoupon,
} from "../lib/whatsapp-newsletter-offer";

// ---------------------------------------------------------------------------
// Wire shapes — mirror the action outputs.
// ---------------------------------------------------------------------------

/** Meta's side of the check — `PreviewWhatsAppNewsletterMeta`. */
interface PreviewMeta {
  readonly status: "ok" | "unreadable";
  readonly unreadable_reason: string | null;
  readonly tier: string | null;
  readonly daily_limit: number | null;
  readonly reserve: number | null;
  readonly messaged_last_24h: number;
  readonly room_today: number | null;
  readonly quality_rating: WhatsAppMetaQualityRating | null;
  readonly can_send_message: WhatsAppMetaCanSendMessage | null;
  readonly send_in_flight_since: string | null;
  readonly verdict: WhatsAppNewsletterMetaVerdict;
  readonly warnings: ReadonlyArray<WhatsAppNewsletterMetaWarning>;
}

interface PreviewResponse {
  readonly configured: boolean;
  readonly not_configured_reason: string | null;
  readonly message: WhatsAppNewsletterMessage;
  readonly template: { readonly name: string; readonly language_code: string } | null;
  readonly article: {
    readonly url: string;
    readonly status: "ok" | "not_found" | "redirects" | "unreachable";
    readonly http_status: number | null;
    readonly location: string | null;
  } | null;
  readonly audience: WhatsAppNewsletterAudience;
  readonly audience_tags: ReadonlyArray<{ readonly slug: string; readonly label: string }> | null;
  readonly audience_newsletters: ReadonlyArray<{ readonly slug: string; readonly title: string }> | null;
  readonly recipient_count: number;
  readonly excluded: Readonly<Record<WhatsAppNewsletterSkipReason, number>>;
  readonly max_recipients: number;
  /** Null when the studio cannot send a newsletter at all. */
  readonly meta: PreviewMeta | null;
  /** When the studio's WhatsApp marketing halt ends (ISO) while one runs, else null. */
  readonly marketing_halted_until: string | null;
  readonly can_send: boolean;
}

/** One message's setup for the studio — `WhatsAppNewsletterMessageSetup`. */
interface MessageSetup {
  readonly message: WhatsAppNewsletterMessage;
  readonly configured: boolean;
  readonly not_configured_reason: string | null;
  readonly template: { readonly name: string; readonly language_code: string } | null;
  /** The article messages' link address, `{{1}}` where the slug goes; null otherwise. */
  readonly article_url_pattern: string | null;
  readonly article_button_index: number | null;
}

interface SendResponse {
  readonly run_id: string;
  readonly status: "queued";
  readonly recipient_count: number;
  /** Null for an `offer`, which links no article. */
  readonly article_url: string | null;
}

interface SendSummary {
  readonly send_id: string;
  /** All three null for an `offer` send, which carries no article. */
  readonly article_slug: string | null;
  readonly article_url: string | null;
  readonly blurb: string | null;
  readonly message: string;
  readonly offer_line: string | null;
  readonly offer_code: string | null;
  readonly audience: string;
  readonly audience_tag_slugs: ReadonlyArray<string> | null;
  readonly audience_tag_labels: ReadonlyArray<string> | null;
  readonly audience_newsletter_slugs: ReadonlyArray<string> | null;
  readonly audience_newsletter_titles: ReadonlyArray<string> | null;
  readonly status: "sending" | "done" | "failed";
  readonly recipient_count: number;
  /** Who the audience left out at send time, by reason; null for a send from before they were kept. */
  readonly left_out_by_reason: Readonly<Record<string, number>> | null;
  readonly sent: number;
  readonly skipped: number;
  readonly failed: number;
  readonly pending: number;
  readonly delivered: number;
  readonly read: number;
  readonly undelivered: number;
  readonly skipped_by_reason: Readonly<Record<string, number>>;
  readonly failed_by_code: Readonly<Record<string, number>>;
  readonly undelivered_by_code: Readonly<Record<string, number>>;
  readonly sent_by_actor: string;
  readonly sent_by_name: string | null;
  readonly last_error: string | null;
  readonly created_at: string;
  readonly completed_at: string | null;
}

// ---------------------------------------------------------------------------
// Copy.
// ---------------------------------------------------------------------------

const AUDIENCE_LABEL: Record<WhatsAppNewsletterAudience, { label: string; hint: string }> = {
  everyone: { label: "Everyone", hint: "every active member" },
  customers: { label: "Customers", hint: "on a trial or a customer" },
  leads: { label: "Leads", hint: "not yet a trial or a customer" },
  tag: { label: "Tag", hint: "members with any of the tags you pick" },
  newsletter: { label: "Newsletter", hint: "everyone the email newsletters you pick went to" },
};

/**
 * The two message choices. "Offer" is `article_offer` with "Include the
 * article" ticked and `offer` without it.
 */
type MessageKind = "article" | "offer";

const MESSAGE_KIND_LABEL: Record<MessageKind, { label: string; hint: string }> = {
  article: { label: "Article", hint: "the article and a nudge to book" },
  offer: { label: "Offer", hint: "an offer, with the article or on its own" },
};

/** The message the two controls make. */
function messageFor(kind: MessageKind, includeArticle: boolean): WhatsAppNewsletterMessage {
  if (kind === "article") return "article";
  return includeArticle ? "article_offer" : "offer";
}

/** `“A”, “B” or “C”` — how a list of names reads in a sentence. */
function orList(names: ReadonlyArray<string>): string {
  const quoted = names.map((n) => `“${n}”`);
  if (quoted.length <= 1) return quoted.join("");
  return `${quoted.slice(0, -1).join(", ")} or ${quoted[quoted.length - 1] ?? ""}`;
}

/** The history's "Who": the audience, or the tags / newsletters it went to. */
function audienceWho(s: SendSummary): string {
  if (s.audience === "tag") {
    const names = s.audience_tag_labels ?? s.audience_tag_slugs ?? [];
    return `${names.length === 1 ? "Tag" : "Tags"}: ${names.join(", ")}`;
  }
  if (s.audience === "newsletter") {
    const names = s.audience_newsletter_titles ?? s.audience_newsletter_slugs ?? [];
    return `${names.length === 1 ? "Newsletter" : "Newsletters"}: ${names.join(", ")}`;
  }
  return AUDIENCE_LABEL[s.audience as WhatsAppNewsletterAudience]?.label ?? s.audience;
}

/** Check's "N members … would receive it" — who the lists name, if any. */
function previewWho(p: PreviewResponse): string {
  if (p.audience_tags !== null) return ` tagged ${orList(p.audience_tags.map((t) => t.label))}`;
  if (p.audience_newsletters !== null) {
    return ` sent ${orList(p.audience_newsletters.map((n) => n.title))}`;
  }
  return "";
}

const SKIP_LABEL: Record<WhatsAppNewsletterSkipReason, string> = {
  member_inactive: "not active",
  do_not_contact: "tagged do-not-contact",
  marketing_opted_out: "opted out of marketing",
  unsubscribed_from_newsletter: "unsubscribed from one of those emails",
  whatsapp_opted_out: "WhatsApp unticked on their profile",
  no_whatsapp_number: "no WhatsApp number",
  number_paused: "number paused: WhatsApp said it isn't on WhatsApp",
  us_number: "US number: Meta doesn't deliver marketing",
  already_received: "already sent this article",
  duplicate_number: "share a WhatsApp number with someone already on the list",
};

/** An offer on its own is left out by its discount code, not an article. */
function skipLabels(message: string): Readonly<Record<WhatsAppNewsletterSkipReason, string>> {
  return message === "offer" ? { ...SKIP_LABEL, already_received: "already sent this code" } : SKIP_LABEL;
}

/**
 * WhatsApp's codes for a message it accepted and then did not deliver — the
 * ones a marketing template meets. Any other code shows as its number.
 */
const UNDELIVERED_LABEL: Readonly<Record<string, string>> = {
  "131049": "held back by WhatsApp's limit on marketing messages to one person",
  "131050": "have stopped marketing messages from the studio in WhatsApp",
  "131026": "not reachable on WhatsApp",
  "130472": "held back by a WhatsApp experiment",
};

/** How often the history is re-read while a send is running. */
const PROGRESS_POLL_MS = 4_000;
/** A queued send whose row never appears stops being waited for after this. */
const QUEUED_WAIT_MS = 120_000;

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    timeZone: studioTimeZone(),
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const fmtCount = (v: number): string => v.toLocaleString("en-GB");

/** "28 Oct" — the day an email newsletter went out, in the studio's zone. */
function fmtDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { timeZone: studioTimeZone(), day: "numeric", month: "short" });
}

/** What to do about a Meta reading that failed. */
function metaUnreadableHint(reason: string | null): string {
  if (reason === "waba_id_missing" || reason === "waba_id_invalid") {
    return "Store the studio's WhatsApp Business Account id as the whatsapp.waba_id secret (Config → Vendor credentials → \"WhatsApp · WABA id (Embedded Signup)\").";
  }
  if (reason === "access_token_missing" || reason === "graph_auth_failed") {
    return "The studio's WhatsApp access token is missing or no longer valid.";
  }
  return "Try again in a few minutes.";
}

const META_WARNING_TEXT: Readonly<Record<WhatsAppNewsletterMetaWarning, string>> = {
  quality_yellow:
    "Meta rates this WhatsApp number's quality yellow. Sending is allowed, but a newsletter that people block or report could push it to red.",
  quality_unknown: "Meta gave no quality rating for this WhatsApp number.",
  number_limited: "Meta says this WhatsApp number is limited in what it can send right now.",
};

function sentBy(s: SendSummary): string {
  if (s.sent_by_name !== null) return s.sent_by_name;
  return s.sent_by_actor.startsWith("system:cli") ? "CLI" : "Staff";
}

function reasonsLine(counts: Readonly<Record<string, number>>, labels: Readonly<Record<string, string>>): string {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .map(([code, n]) => `${String(n)} ${labels[code] ?? code}`)
    .join(" · ");
}

/**
 * The history's Sent and Failed (newsletter-late-refusals, 2026-11-01): a
 * message WhatsApp accepted and then refused counts as Failed, not Sent.
 * `list_whatsapp_newsletter_sends` keeps `sent` as what WhatsApp accepted and
 * `undelivered` as the part of it refused since, and the MCP staff tool reads
 * them as they are; only the page adds them up this way.
 */
export function historyCounts(
  s: Pick<SendSummary, "sent" | "failed" | "undelivered">,
): { readonly sent: number; readonly failed: number } {
  return { sent: s.sent - s.undelivered, failed: s.failed + s.undelivered };
}

/**
 * Failed's hover: what WhatsApp refused when sent, then what it accepted and
 * did not deliver, each on a line of its own. "" when nothing failed.
 */
export function failedLine(s: Pick<SendSummary, "failed_by_code" | "undelivered_by_code">): string {
  const lines: string[] = [];
  const atSend = reasonsLine(s.failed_by_code, {});
  if (atSend !== "") lines.push(`Refused when sent: ${atSend}`);
  const late = Object.entries(s.undelivered_by_code)
    .filter(([, n]) => n > 0)
    .map(([code, n]) => `${String(n)} ${UNDELIVERED_LABEL[code] ?? `WhatsApp code ${code}`}`)
    .join(" · ");
  if (late !== "") lines.push(`Accepted, then not delivered: ${late}`);
  return lines.join("\n");
}

/**
 * The history's "Left out N: …" — who the audience left out at send time, in
 * Check's words and order. "" when it left nobody out, or for a send from
 * before those counts were kept.
 */
function leftOutLine(s: SendSummary): string {
  if (s.left_out_by_reason === null) return "";
  const ordered: Record<string, number> = {};
  for (const r of WHATSAPP_NEWSLETTER_SKIP_REASONS) ordered[r] = s.left_out_by_reason[r] ?? 0;
  // A reason since retired still counts; it shows as its code.
  for (const [r, n] of Object.entries(s.left_out_by_reason)) ordered[r] ??= n;
  const total = Object.values(ordered).reduce((sum, n) => sum + n, 0);
  return total > 0 ? `Left out ${fmtCount(total)}: ${reasonsLine(ordered, skipLabels(s.message))}` : "";
}

// ---------------------------------------------------------------------------
// The page.
// ---------------------------------------------------------------------------

export function Newsletter(): JSX.Element {
  /** What every message opens with — "it's <name>, your EMS studio." */
  const studioName = useWhoami().data?.studio?.name ?? null;
  const [audience, setAudience] = useState<WhatsAppNewsletterAudience>("everyone");
  /** The tags a `tag` audience names — slugs; kept while another audience is picked. */
  const [tagSlugs, setTagSlugs] = useState<ReadonlyArray<string>>([]);
  /** The email newsletters a `newsletter` audience names — edition slugs, likewise kept. */
  const [newsletterSlugs, setNewsletterSlugs] = useState<ReadonlyArray<string>>([]);
  const [articleInput, setArticleInput] = useState("");
  const [blurb, setBlurb] = useState("");
  const [messageKind, setMessageKind] = useState<MessageKind>("article");
  /** "Include the article" under Offer — ticked, the article + offer; unticked, the offer alone. */
  const [includeArticle, setIncludeArticle] = useState(true);
  const message = messageFor(messageKind, includeArticle);
  const hasArticle = whatsAppNewsletterMessageHasArticle(message);
  const hasOffer = whatsAppNewsletterMessageHasOffer(message);
  /**
   * The discount code last picked. It wrote the offer line (the line is what
   * is sent), and it goes with the send: an offer on its own must name it —
   * nobody already sent that code is sent it again — and an article + offer
   * records it so a later offer on its own can leave its recipients out.
   */
  const [offerCode, setOfferCode] = useState<string | null>(null);
  const [offerInput, setOfferInput] = useState("");

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  /** The inputs the preview was made for — any edit invalidates it. */
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  /** One request id per checked preview: a double-click replays, never re-sends. */
  const [sendRequestId, setSendRequestId] = useState<string | null>(null);
  const [typedCount, setTypedCount] = useState("");
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState<{ runId: string; count: number; at: number } | null>(null);

  const [sends, setSends] = useState<ReadonlyArray<SendSummary> | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const slug = whatsAppNewsletterSlugFromInput(articleInput);
  const slugValid =
    slug.length > 0 &&
    slug.length <= WHATSAPP_NEWSLETTER_SLUG_MAX_CHARS &&
    WHATSAPP_NEWSLETTER_SLUG_PATTERN.test(slug);
  const line = normalizeWhatsAppNewsletterBlurb(blurb);
  const blurbValid = line.length > 0 && blurb.length <= WHATSAPP_NEWSLETTER_BLURB_MAX_CHARS;
  // The offer on its own takes neither the article nor the line about it.
  const articleReady = !hasArticle || (slugValid && blurbValid);
  const audienceTagSlugs = audience === "tag" ? tagSlugs : null;
  const audienceNewsletterSlugs = audience === "newsletter" ? newsletterSlugs : null;
  const audienceReady =
    (audienceTagSlugs === null || audienceTagSlugs.length > 0) &&
    (audienceNewsletterSlugs === null || audienceNewsletterSlugs.length > 0);
  // Sent through the blurb's rule — one line, no closing full stop.
  const offer = hasOffer ? normalizeWhatsAppNewsletterBlurb(offerInput) : null;
  const offerValid =
    offer === null || (offer.length > 0 && offerInput.length <= WHATSAPP_NEWSLETTER_OFFER_LINE_MAX_CHARS);
  // The offer on its own must name its code: it is how a repeat is left out.
  const codeReady = message !== "offer" || offerCode !== null;
  const sentCode = hasOffer ? offerCode : null;
  const inputsKey = JSON.stringify([
    audience,
    audienceTagSlugs,
    audienceNewsletterSlugs,
    hasArticle ? slug : null,
    hasArticle ? line : null,
    message,
    offer,
    sentCode,
  ]);

  // Every tag, A–Z, with its active members — loaded once Tag is picked.
  const tagList = useCachedResource("lookup:tags", () => api.lookupOptions("tags"), {
    enabled: audience === "tag",
  });
  const tagOptions = useMemo((): ReadonlyArray<SearchableSelectOption> | null => {
    if (tagList.data === undefined) return null;
    return tagList.data.options.map((o) => ({
      value: o.value,
      label: o.label,
      meta: fmtCount(o.member_count ?? 0),
      keywords: o.value,
    }));
  }, [tagList.data]);

  // The email newsletters that went out — loaded once Newsletter is picked.
  // A test send is not an audience; the action refuses one too.
  const editionList = useCachedResource(
    "newsletter:sent-editions",
    async () => {
      const res = await dispatchAction<{ status: "sent"; limit: number }, NewsletterEditionsList>(
        "list_newsletter_editions",
        { status: "sent", limit: 100 },
      );
      if (!res.ok) throw new Error(res.message);
      return res.response.editions.filter((e) => !e.is_test);
    },
    { enabled: audience === "newsletter" },
  );
  const newsletterOptions = useMemo((): ReadonlyArray<SearchableSelectOption> | null => {
    if (editionList.data === undefined) return null;
    return editionList.data.map((e) => ({
      value: e.slug,
      label: e.title,
      meta: e.sent_at !== null ? fmtDay(e.sent_at) : "",
      keywords: e.slug,
    }));
  }, [editionList.data]);

  // The discount codes an offer may name — loaded once Offer is picked.
  const couponList = useCachedResource(
    "newsletter:offer-codes",
    async () => {
      const res = await dispatchAction<{ limit: number }, { coupons: ReadonlyArray<OfferCoupon> }>(
        "list_stripe_coupons",
        { limit: 100 },
      );
      if (!res.ok) throw new Error(res.message);
      return res.response.coupons;
    },
    { enabled: hasOffer },
  );
  const offerCoupons = useMemo(
    () => (couponList.data === undefined ? null : offerableCoupons(couponList.data, new Date())),
    [couponList.data],
  );
  const couponOptions = useMemo((): ReadonlyArray<SearchableSelectOption> | null => {
    if (offerCoupons === null) return null;
    return offerCoupons.map((c) => ({
      value: c.coupon_id,
      label: c.name !== null ? `${c.coupon_id} — ${c.name}` : c.coupon_id,
      meta: offerOptionMeta(c, studioTimeZone()),
      keywords: c.coupon_id,
    }));
  }, [offerCoupons]);
  const pickOffer = useCallback(
    (code: string): void => {
      const c = offerCoupons?.find((x) => x.coupon_id === code);
      if (c === undefined) return;
      setOfferCode(code);
      setOfferInput(offerLineFromCoupon(c, studioTimeZone()));
    },
    [offerCoupons],
  );
  const previewCurrent = preview !== null && previewKey === inputsKey;

  // Which template each message sends for this studio (newsletter-studio-
  // words, 2026-11-01): the preview shows its words, the hints its address.
  const setupList = useCachedResource("newsletter:whatsapp-setup", async () => {
    const res = await dispatchAction<Record<string, never>, { messages: ReadonlyArray<MessageSetup> }>(
      "get_whatsapp_newsletter_setup",
      {},
    );
    if (!res.ok) throw new Error(res.message);
    return res.response.messages;
  });
  const messageSetup = setupList.data?.find((m) => m.message === message) ?? null;
  const setupTemplate = messageSetup?.template ?? null;
  const words: WhatsAppNewsletterMessageCopy | null =
    setupTemplate !== null ? (WHATSAPP_NEWSLETTER_TEMPLATE_COPY.get(setupTemplate.name) ?? null) : null;
  // The link button's label and the address it opens, `…` where the slug goes.
  const linkButtonIndex = messageSetup?.article_button_index ?? null;
  const linkLabel = words !== null && linkButtonIndex !== null ? (words.buttons[linkButtonIndex] ?? null) : null;
  const linkAddress = messageSetup?.article_url_pattern?.replace("{{1}}", "…") ?? null;

  // An edit throws the check away — the count was for other inputs.
  useEffect(() => {
    if (previewKey !== null && previewKey !== inputsKey) {
      setPreview(null);
      setPreviewKey(null);
      setSendRequestId(null);
      setTypedCount("");
    }
  }, [inputsKey, previewKey]);

  const loadHistory = useCallback(async (): Promise<void> => {
    const res = await dispatchAction<{ limit: number }, { sends: ReadonlyArray<SendSummary> }>(
      "list_whatsapp_newsletter_sends",
      { limit: 20 },
    );
    if (res.ok) {
      setSends(res.response.sends);
      setHistoryError(null);
    } else {
      setHistoryError(res.message);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // Re-read while something is sending, or while a just-queued send has not
  // written its row yet.
  const queuedVisible = queued !== null && (sends ?? []).some((s) => s.send_id === queued.runId);
  const waitingForQueued = queued !== null && !queuedVisible && Date.now() - queued.at < QUEUED_WAIT_MS;
  const anySending = (sends ?? []).some((s) => s.status === "sending");
  const polling = anySending || waitingForQueued;
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!polling) return undefined;
    pollRef.current = setTimeout(() => {
      void loadHistory();
    }, PROGRESS_POLL_MS);
    return () => {
      if (pollRef.current !== null) clearTimeout(pollRef.current);
    };
  }, [polling, sends, loadHistory]);

  // The run's own status catches a send that failed before it wrote its row.
  const run = useSagaRun(queued?.runId ?? null);

  const check = useCallback(async (): Promise<void> => {
    setChecking(true);
    setError(null);
    setQueued(null);
    const res = await dispatchAction<
      {
        audience: WhatsAppNewsletterAudience;
        audience_tag_slugs?: ReadonlyArray<string>;
        audience_newsletter_slugs?: ReadonlyArray<string>;
        article_slug?: string;
        message: WhatsAppNewsletterMessage;
        offer_code?: string;
      },
      PreviewResponse
    >("preview_whatsapp_newsletter", {
      audience,
      ...(audienceTagSlugs !== null ? { audience_tag_slugs: audienceTagSlugs } : {}),
      ...(audienceNewsletterSlugs !== null ? { audience_newsletter_slugs: audienceNewsletterSlugs } : {}),
      ...(hasArticle ? { article_slug: slug } : {}),
      message,
      ...(sentCode !== null ? { offer_code: sentCode } : {}),
    });
    setChecking(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setPreview(res.response);
    setPreviewKey(inputsKey);
    setSendRequestId(res.response.can_send ? generateRequestIdV7() : null);
    setTypedCount("");
  }, [audience, audienceTagSlugs, audienceNewsletterSlugs, hasArticle, slug, message, sentCode, inputsKey]);

  const send = useCallback(async (): Promise<void> => {
    if (preview === null || sendRequestId === null) return;
    setSending(true);
    setError(null);
    const res = await dispatchAction<
      {
        audience: WhatsAppNewsletterAudience;
        audience_tag_slugs?: ReadonlyArray<string>;
        audience_newsletter_slugs?: ReadonlyArray<string>;
        article_slug?: string;
        blurb?: string;
        message: WhatsAppNewsletterMessage;
        offer_line?: string;
        offer_code?: string;
        confirm_recipient_count: number;
      },
      SendResponse
    >(
      "send_whatsapp_newsletter",
      {
        audience,
        ...(audienceTagSlugs !== null ? { audience_tag_slugs: audienceTagSlugs } : {}),
        ...(audienceNewsletterSlugs !== null ? { audience_newsletter_slugs: audienceNewsletterSlugs } : {}),
        ...(hasArticle ? { article_slug: slug, blurb } : {}),
        message,
        ...(hasOffer ? { offer_line: offerInput } : {}),
        ...(sentCode !== null ? { offer_code: sentCode } : {}),
        confirm_recipient_count: preview.recipient_count,
      },
      { requestId: sendRequestId },
    );
    setSending(false);
    if (!res.ok) {
      setError(res.message);
      // The audience moved, or the link changed under us: the only way on is
      // a fresh check, so the stale count cannot be sent.
      setPreview(null);
      setPreviewKey(null);
      setSendRequestId(null);
      setTypedCount("");
      return;
    }
    setQueued({ runId: res.response.run_id, count: res.response.recipient_count, at: Date.now() });
    setPreview(null);
    setPreviewKey(null);
    setSendRequestId(null);
    setTypedCount("");
    void loadHistory();
  }, [
    audience,
    audienceTagSlugs,
    audienceNewsletterSlugs,
    hasArticle,
    hasOffer,
    slug,
    blurb,
    message,
    offerInput,
    sentCode,
    preview,
    sendRequestId,
    loadHistory,
  ]);

  // A pasted link the button would not open — Check looked up the page
  // of the same slug instead, so it must not be sent as if it were that link.
  const linkMismatch =
    preview !== null && preview.article !== null
      ? whatsAppNewsletterPastedLinkMismatch(articleInput, preview.article.url)
      : null;
  const typedMatches =
    preview !== null && typedCount.trim() === String(preview.recipient_count);
  const canSend =
    previewCurrent &&
    preview.can_send &&
    linkMismatch === null &&
    typedMatches &&
    sendRequestId !== null &&
    !sending;

  const exclusions = useMemo(() => {
    if (preview === null) return "";
    const ordered: Record<string, number> = {};
    for (const r of WHATSAPP_NEWSLETTER_SKIP_REASONS) ordered[r] = preview.excluded[r] ?? 0;
    return reasonsLine(ordered, skipLabels(preview.message));
  }, [preview]);

  return (
    <div className="page-newsletter">
      <h1 className="page-title">Newsletter</h1>
      <p className="member-note">
        Send an article to members on WhatsApp, in the words Meta approved for this studio — the
        preview below shows them. Each one is greeted by their first name and told who it is from,
        then gets your line about the article — with a nudge to book, or an offer — and a button
        that opens it. Or send an offer on its own, with a <strong>Tell me more</strong> button that
        starts a chat about it. Every message also carries a <strong>Not interested</strong>{" "}
        button: one tap stops all marketing to that member, and they are told so. A WhatsApp
        message cannot be recalled, so you check first and type the number it will reach. Members
        who opted out or are tagged do-not-contact are never sent it, someone in several of the
        tags or newsletters you pick is sent it once, and nobody is sent the same article — or the
        same offer code — twice.
      </p>
      <p className="member-note subtle">
        The email newsletters, and how each one did, are on <Link to="/campaigns">Campaigns</Link>.
      </p>

      <section className="panel">
        <h2>New newsletter</h2>

        <fieldset className="newsletter-audience" disabled={checking || sending}>
          <legend>Who</legend>
          {WHATSAPP_NEWSLETTER_AUDIENCES.map((a) => (
            <label key={a} className="newsletter-audience-option">
              <input
                type="radio"
                name="newsletter-audience"
                value={a}
                checked={audience === a}
                onChange={() => setAudience(a)}
              />
              <span className="newsletter-audience-label">{AUDIENCE_LABEL[a].label}</span>
              <span className="subtle">{AUDIENCE_LABEL[a].hint}</span>
            </label>
          ))}
          {audience === "tag" && (
            <AudienceListPicker
              picked={tagSlugs}
              options={tagOptions}
              onChange={setTagSlugs}
              max={WHATSAPP_NEWSLETTER_AUDIENCE_TAGS_MAX}
              noun="tag"
              emptyText="This studio has no tags yet."
              loadError={
                tagList.error !== null && tagList.data === undefined
                  ? `The tags could not be loaded: ${tagList.error.message}`
                  : null
              }
              note="active members carrying it"
            />
          )}
          {audience === "newsletter" && (
            <AudienceListPicker
              picked={newsletterSlugs}
              options={newsletterOptions}
              onChange={setNewsletterSlugs}
              max={WHATSAPP_NEWSLETTER_AUDIENCE_NEWSLETTERS_MAX}
              noun="newsletter"
              emptyText="No email newsletter has been sent from Clavis yet."
              loadError={
                editionList.error !== null && editionList.data === undefined
                  ? `The newsletters could not be loaded: ${editionList.error.message}`
                  : null
              }
              note="anyone who unsubscribed from one of them is left out"
            />
          )}
        </fieldset>

        <fieldset className="newsletter-audience" disabled={checking || sending}>
          <legend>Message</legend>
          {(["article", "offer"] as const).map((k) => (
            <label key={k} className="newsletter-audience-option">
              <input
                type="radio"
                name="newsletter-message"
                value={k}
                checked={messageKind === k}
                onChange={() => setMessageKind(k)}
              />
              <span className="newsletter-audience-label">{MESSAGE_KIND_LABEL[k].label}</span>
              <span className="subtle">{MESSAGE_KIND_LABEL[k].hint}</span>
            </label>
          ))}
          {messageKind === "offer" && (
            <label className="newsletter-audience-option newsletter-include-article">
              <input
                type="checkbox"
                checked={includeArticle}
                onChange={(e) => setIncludeArticle(e.target.checked)}
              />
              <span className="newsletter-audience-label">Include the article</span>
              <span className="subtle">
                {includeArticle
                  ? "the article's buttons, and the offer below it"
                  : "the offer on its own, with a Tell me more button"}
              </span>
            </label>
          )}
        </fieldset>

        {hasArticle && (
          <>
            <div className="form-row">
              <label htmlFor="newsletter-article">Article</label>
              <input
                id="newsletter-article"
                type="text"
                value={articleInput}
                disabled={checking || sending}
                placeholder="new-belgravia-studio-kinnerton-street — or paste the article's link"
                onChange={(e) => setArticleInput(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
              {articleInput.trim().length > 0 && !slugValid ? (
                <small className="newsletter-field-error">
                  That is not an article slug — lower-case letters, digits and hyphens, one path
                  segment. Paste the article's link and the slug is taken from it.
                </small>
              ) : (
                <small>
                  {slugValid && slug !== articleInput.trim() ? (
                    <>
                      Slug: <code>{slug}</code>
                    </>
                  ) : linkAddress !== null ? (
                    <>
                      The last part of the address{" "}
                      {linkLabel !== null ? (
                        <>
                          the <strong>{linkLabel}</strong> button opens
                        </>
                      ) : (
                        "the message opens"
                      )}
                      : <code>{linkAddress}</code>
                    </>
                  ) : (
                    "The last part of the article's address."
                  )}
                </small>
              )}
            </div>

            <div className="form-row">
              <label htmlFor="newsletter-blurb">What the article is</label>
              <textarea
                id="newsletter-blurb"
                rows={2}
                value={blurb}
                maxLength={WHATSAPP_NEWSLETTER_BLURB_MAX_CHARS}
                disabled={checking || sending}
                placeholder="how we chose Kinnerton Street for the new studio"
                onChange={(e) => setBlurb(e.target.value)}
              />
              <small>
                {String(blurb.length)}/{String(WHATSAPP_NEWSLETTER_BLURB_MAX_CHARS)} — it finishes
                the sentence below, so start lower-case and leave off the full stop.
              </small>
            </div>
          </>
        )}

        {hasOffer && (
          <div className="form-row newsletter-offer-row">
            <label htmlFor="newsletter-offer">The offer</label>
            <div className="newsletter-audience-tag">
              <SearchableSelect
                options={couponOptions}
                value={offerCode}
                onChange={pickOffer}
                ariaLabel="Discount code"
                placeholder="Choose a discount code"
                searchPlaceholder="Search codes"
                emptyText={
                  hasArticle
                    ? "No discount code anyone may use right now — create one on Discount codes, or write the offer below."
                    : "No discount code anyone may use right now — create one on Discount codes. An offer on its own needs one."
                }
                noMatchText={(q) => `No code matches “${q}”.`}
                loadError={
                  couponList.error !== null && couponList.data === undefined
                    ? `The discount codes could not be loaded: ${couponList.error.message}`
                    : null
                }
              />
            </div>
            <textarea
              id="newsletter-offer"
              rows={3}
              value={offerInput}
              maxLength={WHATSAPP_NEWSLETTER_OFFER_LINE_MAX_CHARS}
              disabled={checking || sending}
              placeholder="15% off packages and memberships with code OCTOBER15, until Saturday 31 October"
              onChange={(e) => setOfferInput(e.target.value)}
            />
            <small>
              {String(offerInput.length)}/{String(WHATSAPP_NEWSLETTER_OFFER_LINE_MAX_CHARS)} — picking
              a code writes this line; edit it as you like. It finishes “This week only: …”, so
              leave off the full stop.
              {!hasArticle &&
                " The code goes with it: nobody already sent this code is sent it again, and the chat agent sells with it when someone taps Tell me more."}
            </small>
          </div>
        )}

        {/* The studio's template's approved words, footer and buttons
            (WHATSAPP_NEWSLETTER_TEMPLATE_COPY, by the name
            get_whatsapp_newsletter_setup answers) — a preview only; Meta
            renders the real message. */}
        {words === null ? (
          <div
            className={
              messageSetup === null && setupList.error === null
                ? "subtle newsletter-notice"
                : "tag tag-error newsletter-notice"
            }
          >
            {messageSetup !== null && !messageSetup.configured
              ? `This studio cannot send ${message === "article" ? "a newsletter" : "the offer message"} yet: ${messageSetup.not_configured_reason ?? ""}`
              : setupTemplate !== null
                ? `This page has no words on file for '${setupTemplate.name}' — reload the page.`
                : setupList.error !== null
                  ? `This studio's message could not be loaded: ${setupList.error.message}`
                  : "Loading this studio's message…"}
          </div>
        ) : (
          <div className="newsletter-message-preview" aria-label="Message preview">
            {whatsAppNewsletterCopyBodyParts(words).map((part, i) =>
              part.kind === "text" ? (
                <span key={i}>{part.text}</span>
              ) : part.variable === "first_name" ? (
                <span key={i}>{"{first name}"}</span>
              ) : part.variable === "studio_name" ? (
                <span key={i}>{studioName ?? "{studio name}"}</span>
              ) : part.variable === "blurb" ? (
                <strong key={i}>{line.length > 0 ? line : "your line about the article"}</strong>
              ) : (
                <strong key={i}>{offer !== null && offer.length > 0 ? offer : "the offer"}</strong>
              ),
            )}
            {words.footer !== null && <div className="newsletter-message-footer">{words.footer}</div>}
            <div className="newsletter-message-buttons">
              {words.buttons.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          </div>
        )}

        <div className="newsletter-actions">
          <button
            type="button"
            onClick={() => void check()}
            disabled={!audienceReady || !articleReady || !offerValid || !codeReady || checking || sending}
          >
            {checking ? "Checking…" : "Check"}
          </button>
          {!audienceReady && (
            <span className="subtle">
              Choose {audience === "tag" ? "a tag" : "a newsletter"} to check.
            </span>
          )}
          {hasArticle && !blurbValid && blurb.length > 0 && (
            <span className="newsletter-field-error">Write a line about the article.</span>
          )}
          {audienceReady && articleReady && !offerValid && (
            <span className="subtle">
              {hasArticle ? "Pick a code or write the offer to check." : "Pick a discount code to check."}
            </span>
          )}
          {audienceReady && offerValid && !codeReady && (
            <span className="subtle">
              Pick the discount code this offer is for — an offer on its own is sent once per code.
            </span>
          )}
        </div>

        {error !== null && <div className="tag tag-error newsletter-notice">{error}</div>}

        {previewCurrent && preview !== null && (
          <div className="newsletter-check">
            {preview.marketing_halted_until !== null && (
              <div className="tag tag-error newsletter-notice">
                WhatsApp marketing is halted until {fmtWhen(preview.marketing_halted_until)}, so no
                newsletter can be sent. Lift the halt on the Config page, or send once it has ended.
              </div>
            )}
            {!preview.configured && (
              <div className="tag tag-error newsletter-notice">
                This studio cannot send {preview.message === "article" ? "a newsletter" : "the offer message"}{" "}
                yet: {preview.not_configured_reason}
              </div>
            )}
            {preview.article !== null && preview.article.status === "ok" && (
              <div className="tag tag-success newsletter-notice">
                The article opens:{" "}
                <a href={preview.article.url} target="_blank" rel="noreferrer">
                  {preview.article.url}
                </a>
              </div>
            )}
            {linkMismatch !== null && (
              <div className="tag tag-error newsletter-notice">
                {linkMismatch.sameSite ? (
                  <>
                    The link you pasted is a different page from the one the message opens. The{" "}
                    <strong>{linkLabel ?? "link"}</strong> button can only open{" "}
                    {linkAddress ?? "the address Meta approved"}, with the last part changed, so
                    members would open {preview.article?.url} — not {linkMismatch.pastedUrl}.
                    Paste that page's own link.
                  </>
                ) : (
                  <>
                    The link you pasted is on {linkMismatch.pastedHost}. The message's{" "}
                    <strong>{linkLabel ?? "link"}</strong> button can only open{" "}
                    {linkAddress ?? "the address Meta approved"} — Meta approved it with that
                    address, and only the last part changes — so Check looked up{" "}
                    {preview.article?.url} instead. To send this piece, publish it there (or a
                    page there that links to it) and paste that page's link.
                  </>
                )}
              </div>
            )}
            {preview.article !== null && preview.article.status === "not_found" && (
              <div className="tag tag-error newsletter-notice">
                {preview.article.url} answers {String(preview.article.http_status)} — there is no
                page at that slug. Check it against the site.
              </div>
            )}
            {preview.article !== null && preview.article.status === "redirects" && (
              <div className="tag tag-warn newsletter-notice">
                {preview.article.url} redirects
                {preview.article.location !== null ? ` to ${preview.article.location}` : ""}, so
                members would land somewhere else. Use the slug of the page it lands on.
              </div>
            )}
            {preview.article !== null && preview.article.status === "unreachable" && (
              <div className="tag tag-error newsletter-notice">
                {preview.article.url}{" "}
                {preview.article.http_status === null
                  ? "did not answer"
                  : `answers ${String(preview.article.http_status)}`}
                . Try again once the site is up.
              </div>
            )}

            <p className="newsletter-count">
              <strong>{preview.recipient_count.toLocaleString("en-GB")}</strong>{" "}
              {preview.recipient_count === 1 ? "member" : "members"}
              {previewWho(preview)} would receive it
              {(preview.audience_tags?.length ?? preview.audience_newsletters?.length ?? 0) > 1
                ? ", each once"
                : ""}
              {exclusions.length > 0 ? ` · not sent to: ${exclusions}` : ""}.
            </p>
            {preview.recipient_count === 0 && (
              <div className="tag tag-warn newsletter-notice">
                Nobody in this audience can be sent this {preview.message === "offer" ? "offer" : "article"} now.
              </div>
            )}
            {preview.recipient_count > preview.max_recipients && (
              <div className="tag tag-warn newsletter-notice">
                That is more than the {preview.max_recipients.toLocaleString("en-GB")} one
                newsletter may reach. Pick a smaller audience.
              </div>
            )}

            {preview.meta !== null && preview.meta.status === "ok" && (
              <p className="newsletter-count newsletter-meta">
                {preview.meta.daily_limit === null ? (
                  <>Meta's daily limit: unlimited</>
                ) : (
                  <>
                    Meta's daily limit: <strong>{fmtCount(preview.meta.daily_limit)}</strong> different
                    people
                  </>
                )}
                {" · "}sent a WhatsApp template in the last 24 hours:{" "}
                <strong>{fmtCount(preview.meta.messaged_last_24h)}</strong>
                {preview.meta.reserve !== null && preview.meta.room_today !== null && (
                  <>
                    {" · "}kept back for cart reminders, bookings and staff alerts:{" "}
                    <strong>{fmtCount(preview.meta.reserve)}</strong>
                    {" · "}room today: <strong>{fmtCount(preview.meta.room_today)}</strong>
                  </>
                )}
                .
              </p>
            )}
            {preview.meta !== null && preview.meta.verdict === "over_limit" && (
              <div className="tag tag-error newsletter-notice">
                This audience is {fmtCount(preview.recipient_count)} — more than the{" "}
                {fmtCount(preview.meta.room_today ?? 0)} Meta has room for today. Send a smaller audience
                now (for example Customers) and the rest at least 24 hours later, or once Check
                shows room.
              </div>
            )}
            {preview.meta !== null && preview.meta.verdict === "unreadable" && (
              <div className="tag tag-error newsletter-notice">
                Meta's messaging limit could not be read ({preview.meta.unreadable_reason}), so
                nothing can be sent right now — a newsletter never goes without it.{" "}
                {metaUnreadableHint(preview.meta.unreadable_reason)}
              </div>
            )}
            {preview.meta !== null && preview.meta.verdict === "send_in_flight" && (
              <div className="tag tag-error newsletter-notice">
                Another newsletter is still sending
                {preview.meta.send_in_flight_since !== null
                  ? ` (queued ${fmtWhen(preview.meta.send_in_flight_since)})`
                  : ""}
                . Send this one once it has finished — the history below shows its progress.
              </div>
            )}
            {preview.meta !== null && preview.meta.verdict === "number_blocked" && (
              <div className="tag tag-error newsletter-notice">
                Meta says this WhatsApp number cannot send messages right now, so nothing can be
                sent.
              </div>
            )}
            {preview.meta !== null && preview.meta.verdict === "quality_red" && (
              <div className="tag tag-error newsletter-notice">
                Meta rates this WhatsApp number's quality red. A newsletter now risks Meta
                restricting the number — which also carries the chat agent and the cart reminders.
                Wait until the rating recovers.
              </div>
            )}
            {preview.meta?.warnings.map((w) => (
              <div key={w} className="tag tag-warn newsletter-notice">
                {META_WARNING_TEXT[w]}
              </div>
            ))}

            {preview.can_send && linkMismatch === null && (
              <div className="newsletter-confirm">
                <label htmlFor="newsletter-confirm-count">
                  Type <strong>{String(preview.recipient_count)}</strong> to send it
                </label>
                <input
                  id="newsletter-confirm-count"
                  type="text"
                  inputMode="numeric"
                  value={typedCount}
                  disabled={sending}
                  onChange={(e) => setTypedCount(e.target.value)}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="newsletter-send"
                  onClick={() => void send()}
                  disabled={!canSend}
                >
                  {sending
                    ? "Sending…"
                    : `Send to ${String(preview.recipient_count)} ${preview.recipient_count === 1 ? "member" : "members"}`}
                </button>
              </div>
            )}
          </div>
        )}

        {queued !== null && (
          <div className="tag tag-success newsletter-notice">
            Sending to {String(queued.count)} {queued.count === 1 ? "member" : "members"} — follow
            it below.
            {run.state === "failed" && run.last_error !== null && (
              <> It stopped: {run.last_error}</>
            )}
            {run.state === "forbidden" && (
              <>
                {" "}
                The send was queued, but checking its progress was refused
                {run.last_error !== null ? ` (${run.last_error})` : ""} — the list below
                still shows how far it has got.
              </>
            )}
          </div>
        )}
      </section>

      <section className="panel">
        <h2>Sent</h2>
        <p className="member-note">
          <strong>Sent</strong> is what WhatsApp accepted and hasn't refused since.{" "}
          <strong>Delivered</strong> and <strong>Read</strong> fill in as members' phones report
          back, so they keep rising for a while — reload to see the latest. WhatsApp can still
          refuse a marketing message after accepting it: it then moves from Sent to{" "}
          <strong>Failed</strong>. Hover Failed to see why.
        </p>
        {historyError !== null && (
          <div className="tag tag-error newsletter-notice">{historyError}</div>
        )}
        {sends === null && historyError === null && <p className="empty">Loading…</p>}
        {sends !== null && sends.length === 0 && (
          <p className="empty">No newsletter has been sent from here yet.</p>
        )}
        {sends !== null && sends.length > 0 && (
          <div className="table-scroll-x">
            <table className="newsletter-history table-cards">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Article</th>
                  <th>Who</th>
                  <th>Sent by</th>
                  <th>Status</th>
                  <th className="num">Sent</th>
                  <th className="num">Delivered</th>
                  <th className="num">Read</th>
                  <th className="num">Skipped</th>
                  <th className="num">Failed</th>
                </tr>
              </thead>
              <tbody>
                {sends.map((s) => (
                  <tr key={s.send_id}>
                    <td data-label="When">{fmtWhen(s.created_at)}</td>
                    <td className="table-card-title">
                      {s.article_url !== null && s.article_slug !== null ? (
                        <>
                          <a href={s.article_url} target="_blank" rel="noreferrer">
                            {s.article_slug}
                          </a>
                          <div className="subtle newsletter-history-blurb">{s.blurb}</div>
                        </>
                      ) : (
                        <span>Offer, no article</span>
                      )}
                      {s.offer_line !== null && (
                        <div className="subtle newsletter-history-blurb">
                          Offer: {s.offer_line}
                          {s.offer_code !== null ? ` (code ${s.offer_code})` : ""}
                        </div>
                      )}
                    </td>
                    <td data-label="Who">
                      {audienceWho(s)}
                      {leftOutLine(s) !== "" && (
                        <div className="subtle newsletter-left-out">{leftOutLine(s)}</div>
                      )}
                    </td>
                    <td data-label="Sent by">{sentBy(s)}</td>
                    <td data-label="Status">
                      {s.status === "sending" ? (
                        <span className="tag tag-warn">
                          Sending · {String(s.pending)} to go
                        </span>
                      ) : s.status === "done" ? (
                        <span className="tag tag-success">Done</span>
                      ) : (
                        <span className="tag tag-error" title={s.last_error ?? undefined}>
                          Stopped
                        </span>
                      )}
                    </td>
                    <td data-label="Sent" className="num">{String(historyCounts(s).sent)}</td>
                    <td data-label="Delivered" className="num">{String(s.delivered)}</td>
                    <td data-label="Read" className="num">{String(s.read)}</td>
                    <td data-label="Skipped" className="num" title={reasonsLine(s.skipped_by_reason, skipLabels(s.message))}>
                      {String(s.skipped)}
                    </td>
                    <td data-label="Failed" className="num" title={failedLine(s) || undefined}>
                      {String(historyCounts(s).failed)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
